import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { notification } from "antd";
import { CheckOutlined } from "@ant-design/icons";
import Icon from "@mdi/react";
import { mdiChevronLeft } from "@mdi/js";
import ContactUsApi from "../../../api-services/contact-us-api.service";
import BasicInfoStep, { BasicInfoValues } from "./components/BasicInfoStep";
import TripPreferenceStep, { TripPreferenceValues } from "./components/TripPreferenceStep";
import TravelDetailsStep, { TravelDetailsValues } from "./components/TravelDetailsStep";
import SuccessView from "./components/SuccessView";

const STEPS = [
  { step: 1, title: "Basic Information", heading: "How can we contact you?" },
  { step: 2, title: "Trip Preferences", heading: "Tell us about your ideal trip" },
  { step: 3, title: "Travel Details", heading: "Your travel details" },
] as const;

const contactUsApi = new ContactUsApi();

function ProgressIndicator({ currentStep }: { currentStep: number }) {
  return (
    <div className="customize-tour-form__progress">
      {STEPS.map(({ step, title }, idx) => {
        const state = step < currentStep ? "done" : step === currentStep ? "active" : "pending";
        return (
          <React.Fragment key={step}>
            <div className={`customize-tour-form__step customize-tour-form__step--${state}`}>
              <span className="customize-tour-form__step-dot">{state === "done" ? <CheckOutlined /> : step}</span>
              <span className="customize-tour-form__step-title">{title}</span>
            </div>
            {idx < STEPS.length - 1 && <span className={`customize-tour-form__step-line customize-tour-form__step-line--${state}`} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function CustomizeTourFormPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const tourCode = searchParams.get("tour_code") || "";
  const destination = searchParams.get("destination") || "";
  const departureDate = searchParams.get("departure_date") || "";

  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [basicInfo, setBasicInfo] = useState<BasicInfoValues | null>(null);
  const [tripPreference, setTripPreference] = useState<TripPreferenceValues | null>(null);

  const handleBasicInfoNext = (values: BasicInfoValues) => {
    setBasicInfo(values);
    setCurrentStep(2);
  };

  const handleTripPreferenceNext = (values: TripPreferenceValues) => {
    setTripPreference(values);
    setCurrentStep(3);
  };

  const handleTravelDetailsSubmit = (values: TravelDetailsValues) => {
    if (!basicInfo || !tripPreference) return;

    setSubmitting(true);
    const totalTravellers = tripPreference.adult + tripPreference.children + tripPreference.infant;

    const payload = {
      designation: basicInfo.designation,
      name: basicInfo.name,
      email: basicInfo.email,
      phone: basicInfo.phone,
      destination,
      tour_code: tourCode,
      departure_date: values.travelDate || departureDate,
      budget: `RM ${values.budget[0].toLocaleString("en-US")} - RM ${values.budget[1].toLocaleString("en-US")}`,
      // Inquiry#message is a required column; "Anything we should know?" is optional in
      // this form, so an empty answer still needs to submit something.
      message: values.message || "No additional notes provided.",
      agent: values.agentName,
      agent_id: values.agentId,
      no_of_travellers: totalTravellers,
      adult: tripPreference.adult,
      children: tripPreference.children,
      infant: tripPreference.infant,
      interest_experience: tripPreference.interests,
      customization: tripPreference.customizations,
      customization_other: tripPreference.customizationOther,
    };

    contactUsApi
      .submitCustomizeTourInquiry(payload)
      .then((success) => {
        if (success) setSubmitted(true);
      })
      .catch(() => {
        notification.error({ message: "An error occured while sending your trip request." });
      })
      .finally(() => setSubmitting(false));
  };

  if (submitted) {
    return (
      <div className="customize-tour-form customize-tour-form--success">
        <SuccessView />
      </div>
    );
  }

  const heading = STEPS[currentStep - 1].heading;

  return (
    <div className="customize-tour-form">
      <div className="customize-tour-form__bg">
        <div className="customize-tour-form__top">
          <div className="customize-tour-form__top-row">
            <button type="button" className="customize-tour-form__back" onClick={() => navigate(-1)}>
              <Icon path={mdiChevronLeft} size="20px" />
              Back
            </button>

            <ProgressIndicator currentStep={currentStep} />
          </div>

          <h2 className="customize-tour-form__heading">{heading}</h2>
        </div>

        <div className="customize-tour-form__card">
          {currentStep === 1 && <BasicInfoStep initialValues={basicInfo} onNext={handleBasicInfoNext} />}
          {currentStep === 2 && (
            <TripPreferenceStep
              initialValues={tripPreference}
              onBack={() => setCurrentStep(1)}
              onNext={handleTripPreferenceNext}
            />
          )}
          {currentStep === 3 && (
            <TravelDetailsStep
              initialDepartureDate={departureDate}
              submitting={submitting}
              onBack={() => setCurrentStep(2)}
              onSubmit={handleTravelDetailsSubmit}
            />
          )}
        </div>
      </div>
    </div>
  );
}
